// Stage 5 C-A — shared portal UI primitives.
// Keeps the visual system consistent (charcoal / off-white / terracotta,
// Manrope headings, Inter body, restrained professional presentation).
import React from "react";

export const CHARCOAL = "#1D1D1B";
export const OFF_WHITE = "#F5F3EE";
export const TERRACOTTA = "#A8543F";
export const BORDER = "#E5E2DC";
export const MUTED = "#6B6B67";

export function PageHeader({ title, kicker, right, testid }) {
    return (
        <div className="mb-6 flex items-start justify-between gap-4" data-testid={testid}>
            <div>
                {kicker && (
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A84] font-manrope font-semibold mb-1">
                        {kicker}
                    </div>
                )}
                <h1 className="font-manrope text-[26px] md:text-[30px] font-semibold text-charcoal leading-tight">
                    {title}
                </h1>
            </div>
            {right && <div>{right}</div>}
        </div>
    );
}

export function Card({ children, className = "", testid }) {
    return (
        <section
            className={`bg-white border border-[#E5E2DC] rounded-md ${className}`}
            data-testid={testid}
        >
            {children}
        </section>
    );
}

export function CardHeader({ title, right, subtle, kicker, testid }) {
    return (
        <div className="px-5 py-3 border-b border-[#EEEBE4] flex items-center justify-between"
             data-testid={testid}>
            <div>
                {kicker && (
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#9A9A94] font-manrope font-semibold">{kicker}</div>
                )}
                <div className={`font-manrope font-semibold ${subtle ? "text-[12px] text-[#6B6B67] uppercase tracking-[0.1em]" : "text-[15px] text-charcoal"}`}>
                    {title}
                </div>
            </div>
            {right && <div>{right}</div>}
        </div>
    );
}

export function Pill({ children, tone = "neutral", testid }) {
    const tones = {
        neutral: "bg-[#EEEBE4] text-[#4A4A46]",
        info:    "bg-[#EEF3F7] text-[#2E5A75]",
        warn:    "bg-[#F8ECDD] text-[#7C4E1A]",
        danger:  "bg-[#F6E0DA] text-[#8C2E1E]",
        ok:      "bg-[#E4EEE4] text-[#2E5A2E]",
        accent:  "bg-[#F2E1DB] text-[#A8543F]",
    };
    return (
        <span className={`inline-flex items-center px-2 py-[3px] rounded-full text-[10.5px] font-manrope font-semibold uppercase tracking-[0.06em] ${tones[tone] || tones.neutral}`}
              data-testid={testid}>
            {children}
        </span>
    );
}

export function Button({ children, onClick, variant = "primary", disabled, testid, type = "button", className = "" }) {
    const base = "inline-flex items-center justify-center px-4 py-2 rounded-md font-manrope font-semibold text-[13.5px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#A8543F]";
    const variants = {
        primary: "bg-[#A8543F] hover:bg-[#8B4535] text-white",
        secondary: "bg-white border border-[#D7D4CC] text-charcoal hover:bg-[#FAF7F1]",
        ghost: "bg-transparent text-charcoal hover:bg-[#EFECE4]",
        danger: "bg-white border border-[#D7D4CC] text-[#8C2E1E] hover:bg-[#F6E0DA]",
    };
    return (
        <button type={type} onClick={onClick} disabled={disabled} data-testid={testid}
                className={`${base} ${variants[variant] || variants.primary} ${className}`}>
            {children}
        </button>
    );
}

export function EmptyState({ heading, note, testid }) {
    return (
        <div className="bg-white border border-dashed border-[#DAD6CD] rounded-md p-10 text-center"
             data-testid={testid}>
            <div className="font-manrope text-[15px] font-semibold text-charcoal mb-2">{heading}</div>
            <div className="text-[13px] text-[#6B6B67] max-w-md mx-auto leading-relaxed">{note}</div>
        </div>
    );
}

export function AccessState({ kind = "unavailable", note, testid }) {
    // §17 — differentiate legitimate state from access error.  Never
    // leak whether a hidden internal resource exists.
    const copy = {
        withdrawn: {
            heading: "This version has been withdrawn",
            note: note || "The version you requested is no longer available. Please use the current version listed against this file.",
            tone: "warn",
        },
        unavailable: {
            heading: "Not available",
            note: note || "This item isn't available on the portal.",
            tone: "neutral",
        },
        unauthorised: {
            heading: "Not available on this portal",
            note: note || "You don't have access to this item.",
            tone: "neutral",
        },
        expired: {
            heading: "Link expired",
            note: note || "This link is no longer valid. Please contact your project team for a fresh link.",
            tone: "neutral",
        },
        temporary: {
            heading: "Temporarily unavailable",
            note: note || "We couldn't load this section just now. Please try again shortly.",
            tone: "warn",
        },
    }[kind] || {};
    return (
        <div className="bg-white border border-[#E5E2DC] rounded-md p-8 text-center max-w-lg mx-auto"
             data-testid={testid}>
            <Pill tone={copy.tone}>{copy.heading}</Pill>
            <div className="mt-4 text-[13.5px] text-[#4A4A46] leading-relaxed">{copy.note}</div>
        </div>
    );
}

export function Spinner({ testid }) {
    return (
        <div className="flex items-center justify-center py-16" data-testid={testid}>
            <div className="animate-pulse text-[13px] text-[#6B6B67] font-manrope">Loading…</div>
        </div>
    );
}

export function Breadcrumbs({ items = [], testid }) {
    return (
        <nav className="mb-4 flex items-center gap-1 text-[12px] text-[#6B6B67] font-manrope" data-testid={testid}>
            {items.map((it, idx) => (
                <React.Fragment key={idx}>
                    {idx > 0 && <span className="text-[#B9B6AC]">›</span>}
                    {it.to ? (
                        <a href={it.to} className="hover:text-charcoal">{it.label}</a>
                    ) : (
                        <span className="text-charcoal font-semibold">{it.label}</span>
                    )}
                </React.Fragment>
            ))}
        </nav>
    );
}

export function AutosaveIndicator({ state, savedAt, testid }) {
    // §10 — quiet autosave state.  No generic Save button anywhere.
    const map = {
        idle:   { label: "", tone: "" },
        saving: { label: "Saving…", tone: "text-[#6B6B67]" },
        saved:  { label: `Last saved ${new Date(savedAt || Date.now()).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`, tone: "text-[#2E5A2E]" },
        error:  { label: "Autosave failed", tone: "text-[#8C2E1E]" },
    };
    const m = map[state] || map.idle;
    return (
        <span className={`text-[11.5px] font-manrope ${m.tone}`} data-testid={testid}>
            {m.label}
        </span>
    );
}
